import { CurrencyCode, GetCartQuery, LineFragmentFragment,} from "../types/storefront.generated";
import {
  ICartBundleItem,
  ICartGiftItem,
  ICheckout,
  ICustomerAddress,
  IFreeGiftTier,
  ILineItem,
  IShippingAddress,
  IShippingMethod,
} from "../types/types";
import Decimal from "decimal.js";
import config from "./config";
import { getHiddenMarketKey } from "./market";
import {
  CheckoutPriceOverrides,
  getBundlePriceOverrides,
  shouldApplyBundlePricing,
} from "./checkoutPriceOverrides";
import { getRegionalBundlePrice, isBundleAvailableForRegion } from "./bundlePricing";
import { ClubPricingContext, getCartClubPricingContext, getLineClubPrice } from "./clubPricing";

export default async function createCheckoutFromCart(
  cart: GetCartQuery["cart"],
) {
  if (!cart) {
    return Promise.reject("No cart found");
  }

  // @TODO Check that cart items are available for sale
  // line.merchandise.availableForSale must be true
  // Likely some more logic required here for preorder items
  // If not, should throw a custom error that can be
  // handled on both front and backend.
  // It should contain a list of *all* out of stock cart items

  const priceOverrides = await getBundlePriceOverrides(cart);
  const pricingContext = getCartClubPricingContext(cart);
  const applyBundlePricing = shouldApplyBundlePricing(pricingContext);

  const filteredLineItems = cart.lines.nodes
    .flatMap((line) =>
      transformLineItem(
        // @ts-ignore
        line,
        pricingContext,
        priceOverrides
      ),
    )
    .filter((item): item is ILineItem => item !== null);


  const availableItems = filteredLineItems.filter(
    (item) => item.availableForSale,
  );

  const unavailableItems = filteredLineItems.filter(
    (item) => !item.availableForSale,
  );

  const cartBundles: ICartBundleItem[] = cart.bundles?.value
    ? JSON.parse(cart.bundles.value)
    : [];
  const activeCartBundles = cartBundles.filter((bundleItem) =>
    isBundleAvailableForRegion(bundleItem.bundle),
  );
  const hasBundleItems = activeCartBundles.length > 0;
  const requiresShipping = hasBundleItems || cart.lines.nodes.some(
    // @ts-ignore
    line => line.merchandise?.requiresShipping !== false
  );

  const checkout: ICheckout = {
    cartId: cart.id,
    customer: null,
    email: null,
    phone: null,
    currencyCode: config.currencyCode as CurrencyCode,
    charityDonation: parseCharityDonation(cart.charityDonation?.value),
    lineItems: availableItems,
    outOfStockLineItems: unavailableItems,
    bundleItems: [],
    giftItems: [],
    freeGiftTier: null,
    availableShippingMethods: [],
    shippingMethod: null,
    shippingAddress: null,
    discountCode: null,
    appliedGiftCards: [],
    paymentMethod: null,
    completed: false,
    emailMarketingOptIn: true,
  };
  if (cart?.giftsWithPurchase?.value) {
    const giftsWithPurchase: ICartGiftItem[] = JSON.parse(
      cart.giftsWithPurchase.value,
    );
    const giftQuantities: { [variantId: string]: number } = {};

    // Aggregate gift quantities by variant ID
    giftsWithPurchase.forEach((giftItem) => {
      const variantId = giftItem.parentProductId;
      if (giftQuantities[variantId]) {
        giftQuantities[variantId]++;
      } else {
        giftQuantities[variantId] = 1;
      }
    });

    // Create a map of line item quantities
    const lineItemsMap: { [variantId: string]: number } = {};
    checkout.lineItems.forEach((lineItem) => {
      lineItemsMap[lineItem.variantId] = lineItem.quantity;
    });

    // Add valid gifts to checkout.giftItems, respecting the line item quantities
    const addedGiftsCount: { [variantId: string]: number } = {};
    giftsWithPurchase.forEach((giftItem) => {
      const variantId = giftItem.parentProductId;
      const lineItemQuantity = lineItemsMap[variantId] || 0;
      const currentAddedCount = addedGiftsCount[variantId] || 0;

      if (currentAddedCount < lineItemQuantity) {
        checkout.giftItems.push({
          id: variantId,
          parentVariantId: giftItem.parentProductId,
          productTitle: giftItem.variant.product.title,
          variantTitle: cleanRolloverStockName(giftItem.variant.title),
          imageUrl: giftItem.variant.image.url ?? "",
          quantity: 1,
          selectedOptions: giftItem.variant.selectedOptions,
        });
        addedGiftsCount[variantId] = currentAddedCount + 1;
      }
    });
  }

  if (activeCartBundles.length > 0) {
    for (const bundleItem of activeCartBundles) {
      const bundlePrice = getRegionalBundlePrice(bundleItem.bundle);
      if (bundlePrice === null) {
        continue;
      }
      const regularPrice = bundleItem.selectedVariants.reduce(
        (total, variant) => total.plus(variant.price.amount),
        new Decimal(0),
      );
      checkout.bundleItems.push({
        id: bundleItem.bundle.id,
        title: bundleItem.bundle.title?.value ?? bundleItem.bundle.product_bundle_builder ?? "",
        imageUrl: bundleItem.bundle.bundle_image ?? "",
        quantity: bundleItem.quantity,
        price: (applyBundlePricing ? bundlePrice : regularPrice).toFixed(2),
        comparePrice: bundleItem.selectedVariants
          .reduce(
            (accumulator, variant) => {
              const price = variant.compareAtPrice?.amount ?? variant.price.amount;
              return accumulator + parseFloat(price);
            }, 0,
          )
          .toFixed(2),
        selectedVariants: bundleItem.selectedVariants.map((variant) => ({
          ...variant,
          title: cleanRolloverStockName(variant.title),
        })),
      });
    }
  }

  if (cart?.freeGiftTier?.value) {
    const freeGiftTier: IFreeGiftTier = JSON.parse(cart?.freeGiftTier?.value);
    if (freeGiftTier) {
      checkout.freeGiftTier = freeGiftTier;
    }
  }

  if (cart?.completed?.value === "true") {
    checkout.completed = true;
  }

  if (cart.buyerIdentity.customer) {
    const customerAddresses = cart.buyerIdentity.customer.addresses.nodes
      .map(transformCustomerAddress)
      .filter((address) => address.country === config.countryCode);

    checkout.customer = {
      customerId: cart.buyerIdentity.customer.id ?? null,
      customerToken: null,
      addresses: customerAddresses,
      email: cart.buyerIdentity.customer.email ?? null,
      phone: cart.buyerIdentity.customer.phone ?? null,
      preferredAddress: null,
    };
  }

  checkout.email =
    cart.buyerIdentity?.email ?? cart.buyerIdentity?.customer?.email ?? null;
  checkout.phone =
    cart.buyerIdentity?.phone ?? cart.buyerIdentity?.customer?.phone ?? null;

  if (cart.delivery.addresses.length > 0) {
    const selectedAddress = cart.delivery.addresses.find((address) => address.selected);
    if (
      selectedAddress &&
      selectedAddress.address.countryCode === config.countryCode &&
      !isDefaultDeliveryAddress(selectedAddress.address)
    ) {
      const preferredAddress = transformShippingAddress(selectedAddress.address);
      const customerAddress = getCustomerAddress(
        preferredAddress,
        checkout.customer?.addresses ?? [],
      );
      if (checkout.customer && customerAddress) {
        checkout.shippingAddress = customerAddress;
      } else {
        checkout.shippingAddress = preferredAddress;
      }
    }
  } else if (checkout.customer && cart.buyerIdentity.customer?.defaultAddress) {
    const preferredAddress = {
      ...transformShippingAddress(cart.buyerIdentity.customer.defaultAddress),
      id: cart.buyerIdentity.customer.defaultAddress.id,
    };

    if (preferredAddress.country === config.countryCode) {
      checkout.customer.preferredAddress = preferredAddress;
    }
  }

  // Extract shipping methods from deliveryGroups if available
  if (cart.deliveryGroups?.nodes && cart.deliveryGroups.nodes.length > 0) {
    const shippingMethods: IShippingMethod[] = [];

    for (const group of cart.deliveryGroups.nodes) {
      if (group.deliveryOptions) {
        for (const option of group.deliveryOptions) {
          if (requiresShipping && option.handle === "digital-delivery") {
            continue;
          }
          shippingMethods.push({
            groupId: group.id,
            handle: option.handle,
            title: option.title ?? 'Standard Shipping',
            description: option.description || undefined,
            price: option.estimatedCost?.amount || '9.99',
            currencyCode: option.estimatedCost?.currencyCode || config.currencyCode,
          });
        }
      }

      // Set selected delivery option if available
      if (group.selectedDeliveryOption && (!requiresShipping || group.selectedDeliveryOption.handle !== "digital-delivery")) {
        checkout.shippingMethod = {
          groupId: group.id,
          handle: group.selectedDeliveryOption.handle,
          title: group.selectedDeliveryOption.title ?? 'Standard Shipping',
          price: group.selectedDeliveryOption.estimatedCost?.amount || '9.99',
          currencyCode: group.selectedDeliveryOption.estimatedCost?.currencyCode || config.currencyCode,
        };
      }
    }

    if (shippingMethods.length > 0) {
      checkout.availableShippingMethods = shippingMethods;

      // If no shipping method is selected yet, select the first one
      if (!checkout.shippingMethod && checkout.availableShippingMethods.length > 0) {
        checkout.shippingMethod = checkout.availableShippingMethods[0];
      }
    }
  }

  // If cart doesn't require shipping, add a digital delivery option
  if (!requiresShipping && checkout.availableShippingMethods.length === 0) {
    const digitalDelivery = {
      groupId: "",
      handle: "digital-delivery",
      title: "Digital Delivery",
      price: "0.00",
      currencyCode: config.currencyCode,
    };
    checkout.availableShippingMethods = [digitalDelivery];
    checkout.shippingMethod = digitalDelivery;
  }

  const appliedDiscountCode = cart.discountCodes.find(
    (code) => (code.applicable && code.code !== "") || config.partnerCodes.includes(code.code.toUpperCase()),
  );
  if (appliedDiscountCode) {
    checkout.discountCode = appliedDiscountCode.code;
  }

  checkout.appliedGiftCards = cart.appliedGiftCards?.map(giftCard => ({
    id: giftCard.id,
    lastCharacters: giftCard.lastCharacters,
    balance: giftCard.balance ? {
      amount: giftCard.balance.amount,
      currencyCode: giftCard.balance.currencyCode
    } : {
      amount: '0.00',
      currencyCode: config.currencyCode
    },
    amountUsed: giftCard.presentmentAmountUsed ? {
      amount: giftCard.presentmentAmountUsed.amount,
      currencyCode: giftCard.presentmentAmountUsed.currencyCode
    } : {
      amount: '0.00',
      currencyCode: config.currencyCode
    }
  })) || [];

  return checkout;
}

function parseCharityDonation(value?: string) {
  try {
    const donation = JSON.parse(value ?? "") as { amount?: unknown };
    return typeof donation.amount === "number" && donation.amount > 0
      ? { amount: donation.amount }
      : null;
  } catch {
    return null;
  }
}

function isCartLine(line: any): line is LineFragmentFragment {
  return line && line.merchandise;
}

function cleanRolloverStockName(value: string): string {
  return value.replace(/\s+\(new\)(?=\s*(?:\/|$))/gi, "").trim();
}

function transformLineItem(
  line: ILineItem | { __typename?: "ComponentizableCartLine" },
  pricingContext: ClubPricingContext,
  priceOverrides: CheckoutPriceOverrides,
): Array<ILineItem | null> {
  if (!isCartLine(line)) {
    // Handle other types or return null
    console.warn("Skipping non-CartLine item:", line);
    return [];
  }

  const hiddenMarketKey = getHiddenMarketKey();
  const hiddenFromMarket = (line.merchandise as any)[hiddenMarketKey];
  const priceOverride = priceOverrides[line.merchandise.id] ?? null;

  const baseLineItem = {
    variantId: line.merchandise.id,
    productId: line.merchandise.product.id,
    productTitle: line.merchandise.product.title,
    variantTitle: cleanRolloverStockName(line.merchandise.title),
    url: `${config.storeUrl}/products/${line.merchandise.product.handle}?variant=${line.merchandise.id}`,
    sku: line.merchandise.sku ?? "",
    image: {
      url: line.merchandise.image?.url,
      altText: line.merchandise.image?.altText ?? null,
    },
    selectedOptions: line.merchandise.selectedOptions,
    requiresShipping: line.merchandise.requiresShipping,
    availableForSale: line.merchandise.availableForSale && hiddenFromMarket?.value !== "true",
  };

  const priceOverrideQuantity = priceOverride
    ? Math.min(priceOverride.quantity, line.quantity)
    : 0;

  if (priceOverride && priceOverrideQuantity < line.quantity) {
    return [
      {
        ...baseLineItem,
        quantity: priceOverrideQuantity,
        price: calculatePrice(line, pricingContext, priceOverride.price),
        comparePrice: calculateComparePrice(line, pricingContext, priceOverride.price),
        discountNote: priceOverride.discountNote ?? null,
      },
      {
        ...baseLineItem,
        quantity: line.quantity - priceOverrideQuantity,
        price: calculatePrice(line, pricingContext, null),
        comparePrice: calculateComparePrice(line, pricingContext, null),
        discountNote: null,
      },
    ];
  }

  const priceOverrideValue = priceOverride?.price ?? null;
  return [{
    ...baseLineItem,
    quantity: line.quantity,
    price: calculatePrice(line, pricingContext, priceOverrideValue),
    comparePrice: calculateComparePrice(line, pricingContext, priceOverrideValue),
    discountNote: priceOverride ? priceOverride.discountNote ?? null : null,
  }];
}

function isDefaultDeliveryAddress(address: {
  firstName?: string | null;
  lastName?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  countryCode?: string | null;
  zip?: string | null;
  phone?: string | null;
}) {
  return (
    !address.firstName?.trim() &&
    !address.lastName?.trim() &&
    !address.address1?.trim() &&
    !address.address2?.trim() &&
    address.city?.trim().toLowerCase() === config.defaultShippingCity.toLowerCase() &&
    address.countryCode === config.countryCode &&
    !address.zip?.trim() &&
    !address.phone?.trim()
  );
}

function transformCustomerAddress(address: {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  provinceCode?: string | null;
  province?: string | null;
  country?: string | null;
  zip?: string | null;
  phone?: string | null;
}): ICustomerAddress {
  return {
    id: address.id,
    firstName: address.firstName ?? "",
    lastName: address.lastName ?? "",
    address1: address.address1 ?? "",
    address2: address.address2 ?? null,
    city: address.city ?? "",
    province: address.province ?? "",
    provinceCode: address.provinceCode ?? "",
    country: address.country ?? "",
    zip: address.zip ?? "",
    phone: address.phone ?? null,
  };
}

function transformShippingAddress(address: {
  firstName?: string | null;
  lastName?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  provinceCode?: string | null;
  province?: string | null;
  country?: string | null;
  countryCode?: string | null;
  zip?: string | null;
  phone?: string | null;
}): IShippingAddress {
  return {
    firstName: address.firstName ?? "",
    lastName: address.lastName ?? "",
    address1: address.address1 ?? "",
    address2: address.address2 ?? null,
    city: address.city ?? "",
    province: address.province ?? address.provinceCode ?? "",
    provinceCode: address.provinceCode ?? "",
    country: address.country ?? address.countryCode ?? "",
    zip: address.zip ?? "",
    phone: address.phone ?? null,
  };
}

function getCustomerAddress(
  address: IShippingAddress,
  addresses: ICustomerAddress[],
) {
  for (const customerAddress of addresses) {
    if (
      customerAddress.firstName === address.firstName &&
      customerAddress.lastName === address.lastName &&
      customerAddress.address1 === address.address1 &&
      customerAddress.address2 === address.address2 &&
      customerAddress.city === address.city &&
      customerAddress.zip === address.zip &&
      customerAddress.country === config.countryCode
    ) {
      return customerAddress;
    }
  }
  return null;
}

function calculatePrice(
  line: LineFragmentFragment,
  pricingContext: ClubPricingContext,
  priceOverride: string | null,
) {
  const { activeClub, customerId, hasDiscountCode, isYotpoDiscount, isClubPricingDiscount } = pricingContext;
  const clubPrice = getLineClubPrice(line, activeClub, customerId, hasDiscountCode, isYotpoDiscount, isClubPricingDiscount);

  if (priceOverride) {
    return clubPrice
      ? Decimal.min(clubPrice, priceOverride).toFixed(2)
      : priceOverride;
  }

  const lineDiscount =
    line.discountAllocations?.[0]?.discountedAmount?.amount ?? null;
  if (lineDiscount && !isClubPricingDiscount) {
    const discountAmount = new Decimal(lineDiscount).div(line.quantity);
    return new Decimal(line.merchandise.price.amount)
      .minus(discountAmount)
      .toFixed(2);
  }
  if (clubPrice) {
    return clubPrice.toFixed(2);
  }
  return line.merchandise.price.amount;
}

function calculateComparePrice(
  line: LineFragmentFragment,
  pricingContext: ClubPricingContext,
  priceOverride: string | null,
) {
  const { activeClub, customerId, hasDiscountCode, isYotpoDiscount, isClubPricingDiscount } = pricingContext;
  const clubPrice = getLineClubPrice(line, activeClub, customerId, hasDiscountCode, isYotpoDiscount, isClubPricingDiscount);
  if (priceOverride) {
    const comparePrice = line.merchandise.compareAtPrice?.amount ?? line.merchandise.price.amount;
    const checkoutPrice = clubPrice ? Decimal.min(clubPrice, priceOverride) : new Decimal(priceOverride);
    return new Decimal(comparePrice).gt(checkoutPrice)
      ? comparePrice
      : null;
  }

  const lineDiscount =
    line.discountAllocations?.[0]?.discountedAmount?.amount ?? null;
  if (lineDiscount && !isClubPricingDiscount) {
    return line.merchandise.compareAtPrice?.amount ?? line.merchandise.price.amount;
  }
  if (clubPrice) {
    const comparePrice = line.merchandise.compareAtPrice?.amount ?? line.merchandise.price.amount;
    return new Decimal(comparePrice).gt(clubPrice) ? comparePrice : null;
  }
  if (
    line.merchandise.compareAtPrice?.amount &&
    line.merchandise.compareAtPrice?.amount !== line.merchandise.price.amount
  ) {
    return line.merchandise.compareAtPrice.amount;
  }
  return null;
}
